import React, { useState } from 'react'
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Chip,
  Alert,
  Skeleton,
  useTheme,
  CircularProgress,
  Tooltip,
  Button
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudDownload as DownloadIcon,
  CloudOff as RemoteIcon
} from '@mui/icons-material'
import { TimelineEntry as TimelineEntryType } from '../../../types/api'
import TimelineEntry from './TimelineEntry'

// Tip pentru studii remote din patient_study_index
export interface RemoteStudy {
  id: string
  study_instance_uid: string
  study_date: string | null
  study_description: string | null
  modalities: string | null
  is_local: boolean
  local_study_id: string | null
  sync_status: 'PENDING' | 'QUEUED' | 'SYNCING' | 'COMPLETED' | 'FAILED' | 'SKIPPED' | null
  sync_priority: 'IMMEDIATE' | 'BACKGROUND' | 'ON_DEMAND' | null
}

interface TimelineSectionProps {
  timeline: TimelineEntryType[]
  loading?: boolean
  onViewReport?: (entry: TimelineEntryType) => void
  // Smart Sync - studii remote
  remoteStudies?: RemoteStudy[]
  loadingRemote?: boolean
  onPullStudy?: (study: RemoteStudy) => Promise<void>
  pullingStudyId?: string | null
}

// Grupează timeline-ul pe anatomie
const groupTimelineByAnatomy = (timeline: TimelineEntryType[]) => {
  const groups: { [key: string]: TimelineEntryType[] } = {}
  
  // Filtrează doar studiile
  const studiesOnly = timeline.filter(entry => entry.type === 'study')
  
  studiesOnly.forEach(entry => {
    const anatomy = entry.anatomy_group || 'ALTELE'
    if (!groups[anatomy]) {
      groups[anatomy] = []
    }
    groups[anatomy].push(entry)
  })
  
  return groups
}

// Sortează anatomiile - cele cu mai multe studii primele
const sortAnatomyGroups = (groups: { [key: string]: TimelineEntryType[] }) => {
  return Object.entries(groups).sort((a, b) => b[1].length - a[1].length)
}

/**
 * TimelineSection - Secțiunea completă de timeline medical
 * 
 * Design:
 * - Grupare pe anatomie cu acordeoane
 * - Expand/Collapse all
 * - Timeline vizual cu linie și dots
 * - Empty state frumos
 */
const TimelineSection: React.FC<TimelineSectionProps> = ({ 
  timeline, 
  loading,
  onViewReport,
  remoteStudies = [],
  loadingRemote = false,
  onPullStudy,
  pullingStudyId
}) => {
  const theme = useTheme()
  const { designSystem } = theme
  const colors = theme.palette.mode === 'dark' 
    ? designSystem.colors.dark 
    : designSystem.colors.light

  const groupedTimeline = groupTimelineByAnatomy(timeline)
  const sortedGroups = sortAnatomyGroups(groupedTimeline)
  
  // State pentru expanded groups
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(sortedGroups.map(([anatomy]) => anatomy)) // Default: toate expanded
  )

  const toggleGroup = (anatomy: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(anatomy)) {
        next.delete(anatomy)
      } else {
        next.add(anatomy)
      }
      return next
    })
  }

  // Loading state
  if (loading) {
    return (
      <Box>
        {[1, 2].map((i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Skeleton variant="rounded" height={56} sx={{ mb: 1 }} />
            <Box sx={{ pl: 4 }}>
              <Skeleton variant="rounded" height={100} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={100} />
            </Box>
          </Box>
        ))}
      </Box>
    )
  }

  // Empty state
  if (timeline.length === 0 || sortedGroups.length === 0) {
    return (
      <Alert 
        severity="info"
        sx={{
          backgroundColor: `${colors.accent.primary}10`,
          border: `1px solid ${colors.accent.primary}30`,
          '& .MuiAlert-icon': {
            color: colors.accent.primary
          }
        }}
      >
        <Typography variant="body2">
          Nu există istoric medical imagistic pentru acest pacient.
        </Typography>
      </Alert>
    )
  }

  return (
    <Box>
      {/* Anatomy Groups */}
      {sortedGroups.map(([anatomy, entries]) => (
        <AnatomyGroup
          key={anatomy}
          anatomy={anatomy}
          entries={entries}
          expanded={expandedGroups.has(anatomy)}
          onToggle={() => toggleGroup(anatomy)}
          onViewReport={onViewReport}
          colors={colors}
          designSystem={designSystem}
        />
      ))}

      {/* Remote Studies Section - dezactivat, migrată la EasyMedical HIS */}
      {(remoteStudies.length > 0 || loadingRemote) && (
        <RemoteStudiesInline
          studies={remoteStudies}
          loading={loadingRemote}
          onPullStudy={onPullStudy}
          pullingStudyId={pullingStudyId}
          colors={colors}
          designSystem={designSystem}
        />
      )}
    </Box>
  )
}

// Sub-component pentru fiecare grup anatomic
interface AnatomyGroupProps {
  anatomy: string
  entries: TimelineEntryType[]
  expanded: boolean
  onToggle: () => void
  onViewReport?: (entry: TimelineEntryType) => void
  colors: any
  designSystem: any
}

const AnatomyGroup: React.FC<AnatomyGroupProps> = ({
  anatomy,
  entries,
  expanded,
  onToggle,
  onViewReport,
  colors,
  designSystem
}) => {
  // Sortează entries după dată (cele mai recente primele)
  const sortedEntries = [...entries].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  )

  return (
    <Box
      sx={{
        mb: 1,
        backgroundColor: colors.background.paper,
        borderRadius: `${designSystem.components.card.borderRadius}px`,
        border: `1px solid ${colors.border.default}`,
        overflow: 'hidden',
      }}
    >
      {/* Group Header - COMPACT */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          cursor: 'pointer',
          backgroundColor: expanded ? colors.background.elevated : colors.background.paper,
          borderBottom: expanded ? `1px solid ${colors.border.default}` : 'none',
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: colors.background.elevated,
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              fontSize: '0.8rem',
            }}
          >
            {anatomy}
          </Typography>
          <Chip
            label={`${entries.length} studiu${entries.length !== 1 ? 'i' : ''}`}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 600,
              backgroundColor: `${colors.accent.primary}15`,
              color: colors.accent.primary,
              '& .MuiChip-label': { px: 0.75 }
            }}
          />
        </Box>
        <IconButton size="small" sx={{ color: colors.text.secondary, p: 0.25 }}>
          {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>

      {/* Group Content - COMPACT */}
      <Collapse in={expanded}>
        <Box sx={{ px: 1, py: 0.5 }}>
          {sortedEntries.map((entry, index) => (
            <TimelineEntry
              key={entry.id}
              entry={entry}
              isLast={index === sortedEntries.length - 1}
              onViewReport={onViewReport}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

// Sub-component pentru studii remote (nedescărcate)
interface RemoteStudiesInlineProps {
  studies: RemoteStudy[]
  loading?: boolean
  onPullStudy?: (study: RemoteStudy) => Promise<void>
  pullingStudyId?: string | null
  colors: any
  designSystem: any
}

const RemoteStudiesInline: React.FC<RemoteStudiesInlineProps> = ({
  studies,
  loading,
  onPullStudy,
  pullingStudyId,
  colors,
  designSystem
}) => {
  // Filtrează doar studiile care NU sunt locale
  const remoteOnly = studies.filter(s => !s.is_local)
  
  if (loading) {
    return (
      <Box sx={{ mt: 3 }}>
        <Skeleton variant="rounded" height={120} />
      </Box>
    )
  }

  if (remoteOnly.length === 0) {
    return null
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return dateStr
    }
  }

  return (
    <Box
      sx={{
        mt: 3,
        backgroundColor: colors.background.paper,
        borderRadius: `${designSystem.components.card.borderRadius}px`,
        border: `1px dashed ${colors.border.default}`,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: `${colors.accent.warning}08`,
          borderBottom: `1px dashed ${colors.border.default}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <RemoteIcon sx={{ color: colors.accent.warning, fontSize: 20 }} />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              fontSize: '0.95rem',
            }}
          >
            Studii disponibile pentru descărcare
          </Typography>
          <Chip
            label={`${remoteOnly.length} ${remoteOnly.length === 1 ? 'studiu' : 'studii'}`}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 600,
              backgroundColor: `${colors.accent.warning}15`,
              color: colors.accent.warning,
              '& .MuiChip-label': { px: 1.5 }
            }}
          />
        </Box>
      </Box>

      {/* Studies List */}
      <Box sx={{ p: 2 }}>
        {remoteOnly.map((study, index) => (
          <Box
            key={study.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              mb: index < remoteOnly.length - 1 ? 1 : 0,
              backgroundColor: colors.background.elevated,
              borderRadius: 1,
              border: `1px solid ${colors.border.default}`,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.text.primary }}>
                  {formatDate(study.study_date)}
                </Typography>
                <Chip
                  label={study.modalities || '?'}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    backgroundColor: `${colors.accent.primary}15`,
                    color: colors.accent.primary,
                  }}
                />
                {study.sync_status === 'SYNCING' && (
                  <Chip
                    icon={<CircularProgress size={10} sx={{ color: 'inherit' }} />}
                    label="Se descarcă..."
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      backgroundColor: `${colors.accent.info}15`,
                      color: colors.accent.info,
                    }}
                  />
                )}
                {study.sync_status === 'FAILED' && (
                  <Chip
                    label="Eșuat"
                    size="small"
                    color="error"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
              <Typography variant="body2" sx={{ color: colors.text.secondary, fontSize: '0.8rem' }}>
                {study.study_description || 'Fără descriere'}
              </Typography>
            </Box>
            
            {/* Buton sau Spinner în funcție de status */}
            {pullingStudyId === study.id || study.sync_status === 'SYNCING' ? (
              // Spinner circular după ce s-a apăsat
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  minWidth: 120,
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress
                    size={36}
                    thickness={4}
                    sx={{ color: colors.accent.primary }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <DownloadIcon sx={{ fontSize: 16, color: colors.accent.primary }} />
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.accent.primary,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }}
                >
                  Se descarcă...
                </Typography>
              </Box>
            ) : (
              // Buton normal
              <Tooltip title="Descarcă studiul">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => onPullStudy?.(study)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    minWidth: 100,
                    borderColor: colors.accent.primary,
                    color: colors.accent.primary,
                    '&:hover': {
                      backgroundColor: `${colors.accent.primary}10`,
                      borderColor: colors.accent.primary,
                    }
                  }}
                >
                  Descarcă
                </Button>
              </Tooltip>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default TimelineSection
